import * as React from 'react';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import EditIcon from '@mui/icons-material/Edit';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import ReceiptIcon from '@mui/icons-material/Receipt';
import PeopleIcon from '@mui/icons-material/People';
import KeyIcon from '@mui/icons-material/Key';
import { Menu, MenuItem, TableCell } from '@mui/material';
import Chip, { ChipStatus } from 'src/frontend-common/components/Chip';
import { CompanyDetail } from 'src/dto/Company';
import { useNavigate } from 'react-router-dom';
import { paths } from 'src/frontend-common/components/Router';
import { useDispatch } from 'react-redux';
import { baseApiWithCompanyId } from 'src/redux/services/baseApi';
import ButtonComponent from 'src/components/ButtonComponent';
import { format } from 'date-fns';
import DeleteIcon from '@mui/icons-material/Delete';
import RestoreFromTrashIcon from '@mui/icons-material/RestoreFromTrash';
import { useDeleteCompanyAdminMutation, useUpdateCompanyAdminMutation } from 'src/redux/services/companiesAdmin';
import { CustomButton } from 'src/pages/Companies/components/style';
import SnackBarCompleteHandler from 'src/components/SnackBarCompleteHandler';
import { setActiveCompany } from 'src/frontend-common/localstorage';

interface Props {
  company: CompanyDetail;
  isShowDeleted?: boolean;
}

const statusToChipVariant = (date: boolean): ChipStatus => (!date ? 'success' : 'error');

export const CompanyCells: React.FC<Props> = ({ company, isShowDeleted }) => {
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [deleteCompany, { isError: deleteCompanyIsError, isSuccess: deleteCompanyIsSuccess, error: deleteCompanyErrorMessage }] =
    useDeleteCompanyAdminMutation();

  const [updateCompanyAdmin, { isError: restoreCompanyIsError, isSuccess: restoreCompanyIsSuccess, error: restoreCompanyErrorMessage }] =
    useUpdateCompanyAdminMutation();

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const getStatusLabel = (date: boolean) => (!date ? 'Active' : 'Inactive');

  const switchToSelectedCompany = () => {
    setActiveCompany({ id: company.id, name: company.name });
    dispatch(baseApiWithCompanyId.util.resetApiState());
  };

  const handleViewUsersClick = () => {
    switchToSelectedCompany();
    navigate(paths.users.routerPath);
  };

  const handleViewAppsClick = () => {
    switchToSelectedCompany();
    navigate(paths.apps.routerPath);
  };
  const handleDeleteCompany = () => {
    deleteCompany({ id: company.id });
  };
  const handleRestoreCompany = () => {
    const restoredCompany = { ...company, deleted_at: '', deleted_by: '' };
    updateCompanyAdmin(restoredCompany);
  };

  const open = Boolean(anchorEl);

  return (
    <>
      <TableCell>{company.name}</TableCell>
      <TableCell>
        <Chip status={statusToChipVariant(!!company.deleted_at)}>{getStatusLabel(!!company.deleted_at)}</Chip>
      </TableCell>
      <TableCell>{company.creation_date ? format(new Date(company.creation_date), 'yyy/MM/dd | HH:mm') : '-'}</TableCell>
      {isShowDeleted && (
        <TableCell>
          {company.deleted_at ? `Delete at ${format(new Date(company.deleted_at), 'yyy/MM/dd | HH:mm')}` : '-'}
          {company.deleted_by && ` by ${company.deleted_by}`}
        </TableCell>
      )}
      <TableCell>
        <ButtonComponent icon={<MoreHorizIcon />} buttontype="icon" onClick={handleOpen} />
        <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
          {!company.deleted_at && (
            <MenuItem onClick={() => navigate(`${paths.companyDetail.prePath}/${company.id}`)}>
              <CustomButton variant="text" color="inherit" startIcon={<EditIcon />} disableRipple>
                Edit
              </CustomButton>
            </MenuItem>
          )}
          {!company.deleted_at && (
            <MenuItem>
              <CustomButton variant="text" color="inherit" startIcon={<AttachMoneyIcon />} disableRipple>
                Add funds
              </CustomButton>
            </MenuItem>
          )}
          {!company.deleted_at && (
            <MenuItem>
              <CustomButton variant="text" color="inherit" startIcon={<ReceiptIcon />} disableRipple>
                Billing
              </CustomButton>
            </MenuItem>
          )}
          {!company.deleted_at && (
            <MenuItem onClick={handleViewUsersClick}>
              <CustomButton variant="text" color="inherit" startIcon={<PeopleIcon />} disableRipple>
                View Users
              </CustomButton>
            </MenuItem>
          )}
          {!company.deleted_at && (
            <MenuItem onClick={handleViewAppsClick}>
              <CustomButton variant="text" color="inherit" startIcon={<KeyIcon />} disableRipple>
                View Apps
              </CustomButton>
            </MenuItem>
          )}
          {!company.deleted_at && (
            <MenuItem onClick={handleDeleteCompany}>
              <CustomButton variant="text" color="error" startIcon={<DeleteIcon />} disableRipple>
                Delete Company
              </CustomButton>
            </MenuItem>
          )}
          {company.deleted_at && (
            <MenuItem onClick={handleRestoreCompany}>
              <CustomButton variant="text" color="inherit" startIcon={<RestoreFromTrashIcon />} disableRipple>
                Restore Company
              </CustomButton>
            </MenuItem>
          )}
        </Menu>
      </TableCell>
      <SnackBarCompleteHandler
        isSuccess={deleteCompanyIsSuccess}
        isError={deleteCompanyIsError}
        error={deleteCompanyErrorMessage}
        successMessage="Company successfully deleted"
      />
      <SnackBarCompleteHandler
        isSuccess={restoreCompanyIsSuccess}
        isError={restoreCompanyIsError}
        error={restoreCompanyErrorMessage}
        successMessage="Company successfully restored"
      />
    </>
  );
};
